import { ICrewAgent, ICrewCustomDataType } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import _ from "lodash";
import { createRef, PureComponent, ReactNode } from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./ai-agent-modals.scss";

const SUB_TYPE_OPTIONS = [
  { value: "str", label: "string" },
  { value: "bool", label: "boolean" },
  { value: "float", label: "float" }
];

const TYPE_OPTIONS = [...SUB_TYPE_OPTIONS, { value: "List", label: "List of" }];

interface IAgentResponseItem {
  fieldName: string;
  type: string;
  subType: string | null;
  description: string;
}

export interface IAiAgentResponseModalProps {
  isOpen: boolean;
  agent?: ICrewAgent;
  agentDefintions?: ICrewCustomDataType[];
  onAgentUpdate: (agent: ICrewAgent, agentDefinitions?: ICrewCustomDataType[]) => void;
  onClose: () => void;
}

export interface IAiAgentResponseModalState {
  definitions: ICrewCustomDataType[];
  isLoading?: boolean;
  selectedDefinitionIndex: number;
  isCustomDtoOpened: boolean;
  /**
   * Where Update stands. It writes into the agent being edited, not to the server, so there is no
   * loading phase — only the ✓ held long enough to be read before the dialog leaves.
   */
  applyStatus?: KuikaButtonStatus;
}

export class AiAgentResponseModal extends PureComponent<IAiAgentResponseModalProps, IAiAgentResponseModalState> {
  formRef = createRef<FormInstance<any>>();

  unsavedResponseData?: IAgentResponseItem[];

  private readonly applyStatus: SaveStatus = new SaveStatus((applyStatus: KuikaButtonStatus): void => {
    this.setState({ applyStatus });
  });

  componentWillUnmount(): void {
    this.applyStatus.dispose();
  }

  constructor(props) {
    super(props);
    this.state = {
      definitions: [],
      selectedDefinitionIndex: -1,
      isCustomDtoOpened: false
    };
  }

  componentDidUpdate(
    prevProps: Readonly<IAiAgentResponseModalProps>,
    prevState: Readonly<IAiAgentResponseModalState>,
    snapshot?: any
  ): void {
    if (prevProps.isOpen === false && this.props.isOpen === true) {
      this.handleLoad();
    }
  }

  getResponses = (obj: { [key: string]: string }): IAgentResponseItem[] => {
    return Object.entries(obj).map(([fieldName, value]) => {
      // Extract description from parentheses
      const descriptionMatch = value.match(/\((.*?)\)/);
      const description = descriptionMatch ? descriptionMatch[1] : "";

      // Extract type and subtype (for List[Type] format)
      const typeMatch = value.match(/^(\w+)(?:\[(.*?)\])?/);
      const type = typeMatch ? typeMatch[1] : "";
      const subType = typeMatch && typeMatch[2] ? typeMatch[2] : null;

      return { fieldName, type, subType, description };
    });
  };

  handleLoad = (isCustomDto = false) => {
    if (!this.formRef.current || !this.props.agent) return;

    let responses: IAgentResponseItem[] = [];
    let definitions: ICrewCustomDataType[] = [];
    let customTypeName: string | undefined;

    if (isCustomDto) {
      this.unsavedResponseData = this.formRef.current.getFieldValue("responses");

      const selectedDefinition: ICrewCustomDataType =
        this.state.selectedDefinitionIndex > -1
          ? this.state.definitions[this.state.selectedDefinitionIndex]
          : { type_name: "", properties: {} };
      if (selectedDefinition) {
        customTypeName = selectedDefinition.type_name;
        responses = this.getResponses(selectedDefinition.properties);
      }
    } else {
      this.unsavedResponseData = undefined;

      responses = this.getResponses(this.props.agent.task?.output_format ?? {});

      const agentDefinitions = this.props.agentDefintions;
      if (agentDefinitions) {
        definitions = agentDefinitions;
      }
      this.setState({
        definitions,
        isCustomDtoOpened: false,
        selectedDefinitionIndex: -1
      });
    }

    this.formRef.current.setFieldsValue({ responses, customTypeName });
  };

  onSubmit = (data: { customTypeName?: string; responses?: IAgentResponseItem[] }) => {
    if (!this.props.agent) return;
    // A second press during the ✓ would write the same response schema twice.
    if (!this.applyStatus.canStart) return;

    try {
      this.setState({ isLoading: true });
      if (!data.responses?.length) {
        KMainFunctions.displayWarningNotification("Please add at least one response parameter.");
        return;
      }
      const agent = _.cloneDeep(this.props.agent);
      const definitions = _.cloneDeep(this.state.definitions);

      const output_format: {
        [key: string]: string;
      } = data.responses.reduce((acc, { fieldName, subType, type, description }) => {
        if (type === "List") {
          type = `${type}[${subType}]`;
        }
        acc[fieldName] = `${type} (${description})`;
        return acc;
      }, {});

      if (this.state.isCustomDtoOpened) {
        this.handleUpdateAiAgentDefinitions({
          type_name: data.customTypeName || "",
          properties: output_format
        });
        return;
      }

      if (!agent.task) {
        agent.task = { description: "", expected_output: "", output_format };
      } else {
        agent.task.output_format = output_format;
      }
      const agentDefinitions = definitions;

      // The agent takes the schema at once; the ✓ is held on Update, then the dialog leaves. The
      // editor no longer closes it from `onAgentUpdate`, so the close waits for the ✓.
      this.props.onAgentUpdate(agent, agentDefinitions);
      this.applyStatus.succeed(this.props.onClose);
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  parameterNameValidator = async (index: number, value?: string) => {
    if (!value) throw new Error("This field is required");
    if (!/^[A-Za-z_-][A-Za-z0-9_-]*$/.test(value)) throw new Error("Please enter a valid parameter key.");

    const { responses } = this.formRef.current?.getFieldsValue();

    if (responses.find((param, i) => param.key === value && i !== index)) {
      throw new Error("This key value is used before.");
    }
  };

  customTypeNameValidator = async (_rule: any, value?: string) => {
    if (!value?.trim()) throw new Error("This field is required");
    if (
      this.props.agentDefintions?.find(
        (definition, i) => definition.type_name === value && i !== this.state.selectedDefinitionIndex
      )
    )
      throw new Error("This custom type name is used before, please type a unique custom type name");
  };

  handleOpenAiAgentDefinition = (selectedDefinitionName?: string) => {
    const selectedDefinitionIndex = selectedDefinitionName
      ? this.state.definitions.findIndex((definition) => definition.type_name === selectedDefinitionName)
      : -1;
    this.setState({ selectedDefinitionIndex, isCustomDtoOpened: true }, () => {
      this.handleLoad(true);
    });
  };

  handleUpdateAiAgentDefinitions = (definition: ICrewCustomDataType) => {
    const definitions = _.cloneDeep(this.state.definitions);
    if (this.state.selectedDefinitionIndex > -1) {
      definitions.splice(this.state.selectedDefinitionIndex, 1, definition);
      if (this.unsavedResponseData) {
        this.unsavedResponseData[this.state.selectedDefinitionIndex].subType = definition.type_name;
      }
    } else {
      definitions.push(definition);
      if (this.unsavedResponseData) {
        this.unsavedResponseData[definitions.length - 1].subType = definition.type_name;
      }
    }
    this.setState({ definitions });
    this.handleCloseCustomDto();
  };

  handleCloseCustomDto = () => {
    this.setState({ selectedDefinitionIndex: -1, isCustomDtoOpened: false }, () => {
      this.formRef.current?.setFieldsValue({
        responses: this.unsavedResponseData
      });
      this.unsavedResponseData = undefined;
    });
  };

  handleClose = () => {
    this.setState({ definitions: [], isCustomDtoOpened: false });
  };

  closeModal = (): void => {
    if (this.state.isLoading) return;
    this.props.onClose();
  };

  render(): ReactNode {
    const selectedDefinition: ICrewCustomDataType | null = this.state.isCustomDtoOpened
      ? this.state.selectedDefinitionIndex > -1
        ? this.state.definitions[this.state.selectedDefinitionIndex]
        : { type_name: "", properties: {} }
      : null;

    const typeOptions = [
      ...TYPE_OPTIONS,
      ...this.state.definitions.map((definition) => ({
        label: definition.type_name,
        value: definition.type_name
      }))
    ];

    const subTypeOptions = [
      ...SUB_TYPE_OPTIONS,
      ...(!this.state.isCustomDtoOpened
        ? this.state.definitions.map((definition) => ({
            label: definition.type_name,
            value: definition.type_name
          }))
        : [])
    ];

    return (
      <>
        <Form ref={this.formRef} onFinish={this.onSubmit}>
          <KModal
            className="kuika-ai-agent-response-modal"
            header={`Agent Response Builder${
              selectedDefinition !== null
                ? selectedDefinition.type_name
                  ? ` / ${selectedDefinition.type_name}`
                  : "/ New Custom Type"
                : ""
            }`}
            showModal={this.props.isOpen}
            closeModal={this.closeModal}
            width={720}
            isLoading={this.state.isLoading}
            footer={
              <KModalFooter
                onCancel={this.state.isCustomDtoOpened ? this.handleCloseCustomDto : undefined}
                submitLabel="Update"
                status={this.state.applyStatus ?? "idle"}
                statusLabels={{ success: "Updated" }}
                onSubmit={() => {
                  this.formRef.current?.submit();
                }}
              />
            }
          >
            <div className="kuika-ai-agent-response-modal__container">
              {this.state.isCustomDtoOpened && (
                <div className="kuika-ai-agent-response-modal__item" key="custom-dto-name">
                  <div className="kuika-ai-agent-response-modal__field">
                    <Form.Item name={"customTypeName"} rules={[{ validator: this.customTypeNameValidator }]}>
                      <KuikaInput
                        placeholder="Custom type name"
                        onKeyDown={(e) => {
                          if (!e.key.match(/^[a-zA-Z0-9_]*$/)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>
              )}

              <Form.List name="responses">
                {(fields, { add, remove }) => (
                  <>
                    {fields.length > 0 &&
                      fields.map(({ name, fieldKey, ...restField }) => (
                        <div className="kuika-ai-agent-response-modal__item" key={name}>
                          <div className="kuika-ai-agent-response-modal__field">
                            <Form.Item
                              name={[name, "fieldName"]}
                              rules={[
                                {
                                  validator: (_rule, value) => this.parameterNameValidator(name, value)
                                }
                              ]}
                              {...restField}
                            >
                              <KuikaInput
                                placeholder="Field name"
                                onKeyDown={(e) => {
                                  if (!e.key.match(/^[a-zA-Z0-9_]*$/)) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                            </Form.Item>
                          </div>
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue, getFieldsValue, setFieldsValue }) => {
                              const formFieldValue = getFieldValue(["responses", name]);
                              const isCustomTypeSelected = this.state.definitions.find(
                                (type) => type.type_name === formFieldValue.type
                              );

                              return (
                                <div className="kuika-ai-agent-response-modal__field">
                                  <Form.Item
                                    name={[name, "type"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "This field is required"
                                      }
                                    ]}
                                    {...restField}
                                  >
                                    <KuikaSelectbox
                                      options={typeOptions}
                                      placeholder="Type"
                                      onChange={(value) => {
                                        if (value !== "List") {
                                          const { responses } = getFieldsValue();
                                          responses[name].subType = undefined;
                                          setFieldsValue({ responses });
                                        }
                                      }}
                                      // dropdownFooter={<KuikaButton type="text">Add Custom Type</KuikaButton>}
                                    />
                                  </Form.Item>
                                  <KuikaButton
                                    onClick={() => this.handleOpenAiAgentDefinition(formFieldValue.type)}
                                    disabled={!isCustomTypeSelected}
                                    isIcon
                                  >
                                    <KuikaSvg type="sidebar-icons" name="settings" />
                                  </KuikaButton>
                                </div>
                              );
                            }}
                          </Form.Item>

                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) => {
                              const formFieldValue = getFieldValue(["responses", name]);

                              const isListOfTypeSelected = formFieldValue.type === "List";
                              const isPrimitiveSubTypeSelected = !!SUB_TYPE_OPTIONS.find(
                                (type) => type.value === formFieldValue.subType
                              );
                              return (
                                <div className="kuika-ai-agent-response-modal__field">
                                  <Form.Item
                                    name={[name, "subType"]}
                                    rules={[
                                      {
                                        required: isListOfTypeSelected,
                                        message: "This field is required"
                                      }
                                    ]}
                                    {...restField}
                                  >
                                    <KuikaSelectbox
                                      options={subTypeOptions}
                                      placeholder="Subtype"
                                      disabled={!isListOfTypeSelected}
                                      // dropdownFooter={<KuikaButton type="text">Add Custom Type</KuikaButton>}
                                    />
                                  </Form.Item>
                                  {!this.state.isCustomDtoOpened && (
                                    <KuikaButton
                                      onClick={() => this.handleOpenAiAgentDefinition(formFieldValue.subType)}
                                      disabled={!isListOfTypeSelected || !!isPrimitiveSubTypeSelected}
                                      isIcon
                                    >
                                      <KuikaSvg type="sidebar-icons" name="settings" />
                                    </KuikaButton>
                                  )}
                                </div>
                              );
                            }}
                          </Form.Item>

                          <div className="kuika-ai-agent-response-modal__field">
                            <Form.Item
                              name={[name, "description"]}
                              rules={[
                                {
                                  required: true,
                                  message: "This field is required"
                                }
                              ]}
                              {...restField}
                            >
                              <KuikaInput placeholder="Description" />
                            </Form.Item>
                          </div>

                          <div className="kuika-ai-agent-response-modal__actions">
                            <KuikaButton type="tertiary" color="neutral" isIcon onClick={() => remove(name)}>
                              <KuikaSvg type="common" name="trash" color="var(--red-5)" />
                            </KuikaButton>
                          </div>
                        </div>
                      ))}

                    <div>
                      <Form.Item noStyle>
                        <KuikaButton
                          htmlType="button"
                          size="sm"
                          type="tertiary"
                          onClick={() => {
                            add({
                              fieldName: "",
                              type: "str",
                              description: ""
                            });
                          }}
                        >
                          Add field
                        </KuikaButton>
                      </Form.Item>
                    </div>
                  </>
                )}
              </Form.List>
            </div>
          </KModal>
        </Form>
      </>
    );
  }
}
